import { useState } from 'react';
import { Spinner } from '@/components/common/Spinner/Spinner';

// Backend NAObject.cs: NA_SENSOR_NAME_STR_LENGTH = 10 (includes null terminator).
export const MAX_SENSOR_NAME_LENGTH = 9;

/** Mirrors mobile's StatusStr = "Pending" shown next to a control while a
 * matching command is still in the device's MT queue (not yet received). */
export function PendingBadge() {
  return (
    <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
      Pending
    </span>
  );
}

export function ToggleSwitch({
  enabled,
  loading,
  onToggle,
}: {
  enabled: boolean;
  loading: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={loading}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:focus:ring-offset-navy-900 disabled:opacity-50 disabled:cursor-not-allowed ${
        enabled ? 'bg-teal-500' : 'bg-slate-300 dark:bg-navy-600'
      }`}
    >
      {loading ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size="sm" />
        </span>
      ) : (
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      )}
    </button>
  );
}

export function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
      <div className="border-b border-slate-200 px-5 py-4 dark:border-navy-700">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-white">{title}</h2>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

export function SettingRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
      <div className="mr-4">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</p>
        {description && (
          <p className="text-xs text-slate-500 dark:text-slate-400">{description}</p>
        )}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export function SelectDropdown({
  value,
  options,
  disabled,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function EditableNameField({
  value,
  maxLength = MAX_SENSOR_NAME_LENGTH,
  pending = false,
  onSave,
}: {
  /** Current display value — pass the pending/optimistic name here when `pending`
   * is true, mirroring mobile's InputObj.TextStr = pendingSensorName behavior. */
  value: string;
  maxLength?: number;
  pending?: boolean;
  onSave: (newValue: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (!editing) {
    return (
      <span className="flex items-center">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setDraft(value);
            setEditing(true);
          }}
          className="flex items-center gap-1 text-sm font-semibold text-slate-800 hover:text-teal-600 dark:text-white dark:hover:text-teal-400"
        >
          <span>{value}</span>
          <svg className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
          </svg>
        </button>
        {pending && <PendingBadge />}
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <input
        autoFocus
        maxLength={maxLength}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="w-24 rounded border border-teal-400 bg-transparent px-1.5 py-0.5 text-sm text-slate-800 dark:text-slate-100 focus:outline-none"
      />
      <button type="button" disabled={saving} onClick={commit} className="text-teal-500 hover:text-teal-400 disabled:opacity-50">
        {saving ? '…' : '✓'}
      </button>
      <button type="button" onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-300">✕</button>
    </span>
  );
}
